import React, { useEffect, useState } from 'react';

interface SportRole {
  id: number;
  role_name: string;
}
interface Sport {
  id: number;
  name: string;
}
interface SportRoleScreenProps {
  apiBaseUrl: string;
  accessToken: string | null;
  userRoles?: string[];
  userSports?: string[];
}

function getUserSports(roles: string[] | undefined): string[] {
  if (!roles || !Array.isArray(roles)) return [];
  return roles
    .filter(r => typeof r === 'string' && r.startsWith('sports_admin_'))
    .map(r => r.replace('sports_admin_', '').toLowerCase());
}

function hasRole(roles: string[] | undefined, role: string): boolean {
  if (!roles || !Array.isArray(roles)) return false;
  return roles.map(r => r.toLowerCase()).includes(role.toLowerCase());
}

const SportRoleScreen: React.FC<SportRoleScreenProps> = ({ apiBaseUrl, accessToken, userRoles, userSports }) => {
  const [sports, setSports] = useState<Sport[]>([]);
  const [selectedSportId, setSelectedSportId] = useState<number | null>(null);
  const [roles, setRoles] = useState<SportRole[]>([]);
  const [roleInput, setRoleInput] = useState('');
  const [roleError, setRoleError] = useState('');
  const [roleSaving, setRoleSaving] = useState(false);

  useEffect(() => {
    console.log('Fetching sports list...');
    fetch(`${apiBaseUrl}/sports`, {
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
      },
    })
      .then(res => {
        console.log('Sports list response:', res);
        return res.json();
      })
      .then(data => {
        console.log('Sports list data:', data);
        setSports(Array.isArray(data) ? data : []);
      });
  }, [apiBaseUrl, accessToken]);

  useEffect(() => {
    if (selectedSportId) {
      console.log('Fetching roles for sport:', selectedSportId);
      fetch(`${apiBaseUrl}/sports/${selectedSportId}/roles`, {
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
        },
      })
        .then(res => {
          console.log('Roles response:', res);
          return res.json();
        })
        .then(data => {
          console.log('Roles data:', data);
          setRoles(Array.isArray(data) ? data : []);
        });
    } else {
      setRoles([]);
    }
  }, [selectedSportId, apiBaseUrl, accessToken]);

  // Filter sports for dropdown: admins/trustees see all, sports admins see only their sports
  const sportsForUser = hasRole(userRoles, 'admin') || hasRole(userRoles, 'trustee')
    ? sports
    : sports.filter(s => (userSports || getUserSports(userRoles)).includes(s.name.toLowerCase()));

  const handleAddRole = async (e: React.FormEvent) => {
    e.preventDefault();
    setRoleError('');
    setRoleSaving(true);
    try {
      const response = await fetch(`${apiBaseUrl}/sports/${selectedSportId}/roles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({ role_name: roleInput }),
      });
      if (response.ok) {
        setRoleInput('');
        // Refresh roles
        fetch(`${apiBaseUrl}/sports/${selectedSportId}/roles`, {
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
          },
        })
          .then(res => res.json())
          .then(data => setRoles(Array.isArray(data) ? data : []));
      } else {
        setRoleError('Failed to add role');
      }
    } catch {
      setRoleError('Network error');
    } finally {
      setRoleSaving(false);
    }
  };

  const handleEditRole = async (roleId: number, newName: string) => {
    setRoleError('');
    setRoleSaving(true);
    try {
      const response = await fetch(`${apiBaseUrl}/sports/roles/${roleId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({ role_name: newName }),
      });
      if (response.ok) {
        fetch(`${apiBaseUrl}/sports/${selectedSportId}/roles`, {
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
          },
        })
          .then(res => res.json())
          .then(data => setRoles(Array.isArray(data) ? data : []));
      } else {
        setRoleError('Failed to edit role');
      }
    } catch {
      setRoleError('Network error');
    } finally {
      setRoleSaving(false);
    }
  };

  const handleDeleteRole = async (roleId: number) => {
    setRoleError('');
    setRoleSaving(true);
    try {
      const response = await fetch(`${apiBaseUrl}/sports/roles/${roleId}`, {
        method: 'DELETE',
        headers: {
          ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
        },
      });
      if (response.ok) {
        fetch(`${apiBaseUrl}/sports/${selectedSportId}/roles`, {
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
          },
        })
          .then(res => res.json())
          .then(data => setRoles(Array.isArray(data) ? data : []));
      } else {
        setRoleError('Failed to delete role');
      }
    } catch {
      setRoleError('Network error');
    } finally {
      setRoleSaving(false);
    }
  };

  return (
    <div className="py-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-800">Sport Roles</h2>
      <div className="mb-4">
        <label className="block text-gray-700 text-sm font-bold mb-2">Select Sport</label>
        <select
          className="border rounded px-2 py-1 text-sm"
          value={selectedSportId ?? ''}
          onChange={e => setSelectedSportId(Number(e.target.value))}
        >
          <option value="">Select</option>
          {sportsForUser.map(sport => (
            <option key={sport.id} value={sport.id}>{sport.name}</option>
          ))}
        </select>
      </div>
      {selectedSportId && (
        <table className="min-w-full bg-white border border-gray-200 mb-6">
          <thead className="bg-gray-200">
            <tr>
              <th className="py-2 px-4 text-left">Role Name</th>
              <th className="py-2 px-4 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {roles.map(role => (
              <tr key={role.id}>
                <td className="py-2 px-4">
                  <input
                    type="text"
                    className="border rounded px-2 py-1 text-sm"
                    defaultValue={role.role_name}
                    onBlur={e => {
                      if (e.target.value !== role.role_name) handleEditRole(role.id, e.target.value);
                    }}
                    disabled={roleSaving}
                  />
                </td>
                <td className="py-2 px-4">
                  <button
                    type="button"
                    className="bg-red-500 hover:bg-red-700 text-white text-xs px-2 py-1 rounded"
                    onClick={() => handleDeleteRole(role.id)}
                    disabled={roleSaving}
                  >Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {selectedSportId && (
        <form onSubmit={handleAddRole} className="flex gap-2 mb-4">
          <input
            type="text"
            className="border rounded px-2 py-1 text-sm"
            value={roleInput}
            onChange={e => setRoleInput(e.target.value)}
            placeholder="Add new role"
            disabled={roleSaving}
          />
          <button
            type="submit"
            className="bg-green-500 hover:bg-green-700 text-white text-xs px-2 py-1 rounded"
            disabled={roleSaving || !roleInput.trim()}
          >Add</button>
        </form>
      )}
      {roleError && <div className="text-red-600 text-xs mt-2">{roleError}</div>}
    </div>
  );
};

export default SportRoleScreen;
